import EventEmitter from "eventemitter3";
import { Data } from "../../../../../models/data";
import { Context } from "../../../../../models/context";
import { LocalizationService } from "../../../../../services/localization-service";
import { HalfModifierSelection } from "../../../../../types/enums";
import { EventTypes } from "../../../../../core/event-bus";
import { ICustomizeItemModifierBaseElement, ICustomizeItemModifierClickEvent, ICustomizeItemModifierModel, ICustomizeItemModifierModules, ICustomizeItemSizeChangeClickEvent } from "./customize-item-modifier-base.interface";

export abstract class CustomizeItemModifierBase<TParams extends ICustomizeItemModifierBaseElement> {
    protected readonly _eventBus: EventEmitter;
    protected readonly _data: Data;
    protected readonly _context: Context;
    protected readonly _localizationService: LocalizationService;
    protected readonly _modules: ICustomizeItemModifierModules = { premodifierBar: null, halfBar: null };
    protected readonly _item: ICustomizeItemModifierModel;
    protected _containerElement: HTMLElement;

    /**
     * @param {EventEmitter} eventBus - Event bus for communicating UI interactions.
     * @param {Data} data - The global data store for menu/modifiers/etc.
     * @param {Context} context - Runtime context for the current session/view.
     * @param {LocalizationService} localizationService - Service for localized text.
     * @param {ICustomizeItemModifierElement} params - The config data for this modifier.
     */
    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        params: TParams
    ) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._item = this._createModel(params);
        this._containerElement = this._createModifierOptionWithHelperBars(params);
    }

    /**
     * Gets the HTML element representing this modifier.
     * @returns {HTMLElement} The rendered container element.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Gets the ID of the modifier.
     * @returns {number} Modifier ID.
     */
    public get modifierId(): number {
        return this._item.modifierId;
    }

    /**
     * Gets the modifier display group ID or 0 if not available.
     * @returns {number | null} The display group ID.
     */
    public get displayGroupId(): number | null {
        return this._item.modifierDisplayGroup?.ModifierDisplayGroupId ?? 0;
    }

    /**
     * Clears the current half bar element if it exists.
     */
    public clearHalfBar(): void {
        this._modules.halfBar?.clearElement();
    }

    /**
     * Creates the half bar UI if the modifier group is halfable.
     *
     * @param {boolean} hidden - Whether the half bar should be initially hidden.
     */
    public createHalfBar(hidden: boolean): void {
        if (this._item.modifierDisplayGroup?.IsHalfable)
            this._modules.halfBar?.createElement(hidden);
    }

    protected abstract _createModifierOption(params: TParams): HTMLElement;

    protected abstract _addModifierAdditionalElements(
        optionDiv: HTMLElement,
        params: TParams
    ): void;

    /**
     * Builds the internal model used by this component.
     *
     * @param {ICustomizeItemModifierElement} params - Input config for this modifier.
     * @returns {ICustomizeItemModifierModel} The constructed internal model.
     */
    protected _createModel(params: TParams): ICustomizeItemModifierModel {
        return {
            modifierDisplayGroup: params.modifierDisplayGroup,
            modifierId: params.modifierId,
            isHalfMode: params.isHalfMode
        };
    }

    /**
     * Creates the DOM structure for this modifier, including any helper bars (premodifier, halfable).
     *
     * @param {TParams} params - The full configuration object.
     * @returns {HTMLElement} The DOM element for this modifier.
     */
    protected _createModifierOptionWithHelperBars(params: TParams): HTMLElement {
        const modifierElement = this._createModifierOption(params);

        const element = document.createElement("div");
        element.id = `div_customize_item_modifier_${params.modifierDisplayGroup?.ModifierDisplayGroupId ?? ""}_${params.modifierId}`;
        element.appendChild(modifierElement);

        if (params.modifierDisplayGroup && (params.leftModifier || params.rightModifier))
            this._addModifierAdditionalElements(element, params);

        return element;
    }

    /**
    * Emits an event when a non-size modifier is clicked.
    * 
    * @param {string} id - The HTML element ID of the clicked modifier.
    * @param {number | null} modifierDisplayGroupId - The ID of the display group the modifier belongs to.
    * @param {number} modifierId - The ID of the selected modifier.
    * @param {boolean} [isMultiSelect=false] - Whether the group allows multiple selections.
    */
    protected _emitModifierClick(
        id: string,
        modifierDisplayGroupId: number | null,
        modifierId: number,
        halfId: HalfModifierSelection,
        isMultiSelect = false
    ): void {
        const params: ICustomizeItemModifierClickEvent = {
            elementId: id,
            displayGroupId: modifierDisplayGroupId,
            modifierId,
            halfId,
            isMultiSelect
        };

        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_MODIFIER_CLICKED, params);
    }

    /**
     * Emits an event when a size option is clicked.
     * 
     * @param {number} sizeId - The ID of the selected size.
     */
    protected _emitSizeClick(sizeId: number): void {
        const params: ICustomizeItemSizeChangeClickEvent = {
            sizeId
        };

        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_MODIFIER_SIZE_CLICKED, params);
    }
}